import { useState, useEffect } from 'react';
import { Plus, Send, Edit2, Trash2, ChevronUp, ChevronDown, AlertTriangle, Filter, EyeOff } from 'lucide-react';
import { newReleasesApi } from '../../../services/adminApi';
import NewReleasesItemModal from '../NewReleasesItemModal';
import CreateListModal from '../CreateListModal';

const NewReleases = () => {
  const [lists, setLists] = useState([]);
  // Months created but not yet persisted (a list is born when its first book is
  // added), kept here so a freshly created empty month stays on screen.
  const [pendingMonths, setPendingMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    month: '',
  });

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentListContext, setCurrentListContext] = useState(null);

  // Load lists on component mount and when filters change
  useEffect(() => {
    loadLists();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLists = async () => {
    try {
      setLoading(true);
      setError(null);
      // Pass filters without month to backend, filter month on frontend for V1 simplicity
      const backendFilters = { ...filters };
      delete backendFilters.month;

      const result = await newReleasesApi.getNewReleasesLists(backendFilters);
      let lists = result.lists || [];

      // Apply month filter on frontend if specified
      if (filters.month) {
        lists = lists.filter(list => list.month === filters.month);
      }

      setLists(lists);
      // Drop any pending month that now exists as a real (persisted) list.
      const persistedMonths = new Set(lists.map(list => list.month));
      setPendingMonths(prev => prev.filter(month => !persistedMonths.has(month)));
    } catch (err) {
      console.error('Failed to load New Releases lists:', err);
      setError(err.message || 'Failed to load lists');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleAddItem = (listContext) => {
    setCurrentListContext(listContext);
    setEditingItem(null);
    setShowItemModal(true);
  };

  const handleEditItem = (item, listContext) => {
    setCurrentListContext(listContext);
    setEditingItem(item);
    setShowItemModal(true);
  };

  const handleRemoveItem = async (item) => {
    const itemTitle = item.title || `Book ID ${item.book_id}`;
    if (!confirm(`Remove "${itemTitle}" from the New Releases list?`)) {
      return;
    }

    try {
      await newReleasesApi.removeNewReleasesItem(item.id);
      await loadLists();
    } catch (err) {
      console.error('Failed to remove item:', err);
      const errorMessage = err.message || 'Unknown error occurred';
      if (errorMessage.includes('not found')) {
        setError(`The item "${itemTitle}" could not be found. It may have already been removed.`);
      } else {
        setError(`Failed to remove "${itemTitle}": ${errorMessage}`);
      }
    }
  };

  const handleListCreated = (month) => {
    if (month) {
      setPendingMonths(prev => (prev.includes(month) ? prev : [...prev, month]));
    }
    loadLists();
  };

  const handleMoveItem = async (item, listContext, direction) => {
    const currentList = getCurrentList(listContext);
    if (!currentList) return;

    const currentIndex = currentList.items.findIndex(i => i.id === item.id);
    if (currentIndex === -1) return;

    // Can't move beyond boundaries
    if ((direction === 'up' && currentIndex === 0) ||
        (direction === 'down' && currentIndex === currentList.items.length - 1)) {
      return;
    }

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const targetItem = currentList.items[targetIndex];

    try {
      // Swap the two items' ranks — integer ranks, no fractional drift.
      await newReleasesApi.updateNewReleasesItem(item.id, { rank: targetItem.rank });
      await newReleasesApi.updateNewReleasesItem(targetItem.id, { rank: item.rank });
      await loadLists();
    } catch (err) {
      console.error('Failed to reorder item:', err);
      const itemTitle = item.title || `Book ID ${item.book_id}`;
      const errorMessage = err.message || 'Unknown error occurred';
      setError(`Failed to reorder "${itemTitle}": ${errorMessage}`);
    }
  };

  const handlePublishList = async (listContext) => {
    const confirmMessage = `Publish the New Releases list for ${listContext.month}?\n\nThis will make it active and visible to users.`;

    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      await newReleasesApi.publishNewReleasesList({ month: listContext.month });
      await loadLists();
    } catch (err) {
      console.error('Failed to publish list:', err);
      setError(`Failed to publish list: ${err.message}`);
    }
  };

  const getCurrentList = (listContext) => {
    return lists.find((list) => list.id === listContext.id);
  };

  const handleMakeListPassive = async (listContext) => {
    const confirmMessage = `Archive the New Releases list for ${listContext.month}?\n\nThis will hide it from users but keep the data.`;

    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      await newReleasesApi.makeNewReleasesListPassive({ month: listContext.month });
      await loadLists();
    } catch (err) {
      console.error('Failed to make list passive:', err);
      setError(`Failed to make list passive: ${err.message}`);
    }
  };

  // Real lists plus synthetic empty cards for months created but not yet filled.
  const getDisplayLists = () => {
    const existingMonths = new Set(lists.map(list => list.month));
    const syntheticDrafts = pendingMonths
      .filter(month => !existingMonths.has(month))
      .filter(month => !filters.month || month === filters.month)
      .map(month => ({
        id: month,
        month,
        status: 'draft',
        items: [],
        pending: true
      }));

    return [...lists, ...syntheticDrafts];
  };

  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();

    // Generate last 6 months and next 6 months for easy selection
    for (let i = -6; i <= 6; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      const monthValue = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ value: monthValue, label: monthLabel });
    }

    return options;
  };

  const getStatusBadge = (status) => {
    const baseClasses = 'px-2 py-1 text-xs rounded-full';
    if (status === 'active') {
      return `${baseClasses} bg-green-100 text-green-800`;
    } else if (status === 'draft') {
      return `${baseClasses} bg-yellow-100 text-yellow-800`;
    }
    return `${baseClasses} bg-gray-100 text-gray-800`;
  };

  const getListSubtitle = (list) => {
    if (list.age_group) {
      return `Ages ${list.age_group}`;
    }

    return 'Suitability is derived from each book’s canonical reading age';
  };

  const renderListItems = (list, isEditable = false) => {
    if (list.items.length === 0) {
      return (
        <div className="p-8 text-center text-text-secondary">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Filter className="w-6 h-6 text-gray-400" />
          </div>
          <p className="mb-4">No books in this list yet.</p>
          {isEditable && (
            <button
              onClick={() => handleAddItem(list)}
              className="btn-primary text-sm"
            >
              Add First Book
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="divide-y divide-gray-200">
        {list.items.map((item, index) => (
          <div key={item.id} className="p-4 hover:bg-gray-50">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                {item.cover_url ? (
                  <img
                    src={item.cover_url}
                    alt={item.title}
                    className="w-12 h-16 object-cover rounded"
                  />
                ) : (
                  <div className="w-12 h-16 bg-gray-200 rounded flex items-center justify-center">
                    <span className="text-gray-400 text-xs">No Image</span>
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-500">#{item.rank}</span>
                      <h4 className="font-medium text-text-primary">{item.title || `Book ID ${item.book_id}`}</h4>
                    </div>
                    {item.author && (
                      <p className="text-sm text-text-secondary mt-1">{item.author}</p>
                    )}
                    {item.note && (
                      <p className="text-xs text-text-secondary mt-2 italic">&ldquo;{item.note}&rdquo;</p>
                    )}
                  </div>

                  {isEditable && (
                    <div className="flex items-center space-x-1 ml-4">
                      <button
                        onClick={() => handleMoveItem(item, list, 'up')}
                        disabled={index === 0}
                        className="p-1 text-text-secondary hover:text-primary-600 hover:bg-primary-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleMoveItem(item, list, 'down')}
                        disabled={index === list.items.length - 1}
                        className="p-1 text-text-secondary hover:text-primary-600 hover:bg-primary-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditItem(item, list)}
                        className="p-1 text-text-secondary hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit item"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRemoveItem(item)}
                        className="p-1 text-text-secondary hover:text-red-600 hover:bg-red-50 rounded"
                        title="Remove item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary mb-2">New Releases</h1>
            <p className="text-text-secondary">Manage monthly New Releases lists</p>
          </div>
        </div>
        <div className="bg-bg-surface rounded-lg border border-gray-200 p-12 text-center">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading New Releases...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary mb-2">New Releases</h1>
            <p className="text-text-secondary">Manage monthly New Releases lists</p>
          </div>
        </div>
        <div className="bg-bg-surface rounded-lg border border-red-200 p-6">
          <div className="flex items-center space-x-3 text-red-600 mb-4">
            <AlertTriangle className="w-5 h-5" />
            <h3 className="font-medium">Error Loading Lists</h3>
          </div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadLists}
            className="btn-secondary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const displayLists = getDisplayLists();
  const activeLists = displayLists.filter(list => list.status === 'active');
  const draftLists = displayLists.filter(list => list.status === 'draft');
  const passiveLists = displayLists.filter(list => list.status === 'passive');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">New Releases</h1>
          <p className="text-text-secondary">Manage monthly New Releases lists</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create New List</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-bg-surface rounded-lg border border-gray-200 p-4">
        <div className="flex items-center space-x-4">
          <select
            value={filters.month}
            onChange={(e) => handleFilterChange('month', e.target.value)}
            className="border border-gray-200 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="">All Months</option>
            {generateMonthOptions().map(month => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Search books..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="border border-gray-200 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      {/* Active Lists */}
      {activeLists.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-text-primary mb-4">Active Lists</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {activeLists.map(list => (
              <div key={list.id} className="bg-bg-surface rounded-lg border border-gray-200">
                <div className="p-4 border-b border-gray-200 bg-green-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-text-primary">
                        {new Date(list.month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                      </h3>
                      <p className="text-sm text-text-secondary">{getListSubtitle(list)}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={getStatusBadge(list.status)}>
                        {list.status}
                      </span>
                      <button
                        onClick={() => handleMakeListPassive(list)}
                        className="flex items-center space-x-1 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                        title="Make passive"
                      >
                        <EyeOff className="w-3 h-3" />
                        <span>Make Passive</span>
                      </button>
                    </div>
                  </div>
                </div>
                {renderListItems(list, false)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Draft Lists */}
      {draftLists.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-text-primary mb-4">Draft Lists</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {draftLists.map(list => (
              <div key={list.id} className="bg-bg-surface rounded-lg border border-gray-200">
                <div className="p-4 border-b border-gray-200 bg-yellow-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-text-primary">
                        {new Date(list.month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                      </h3>
                      <p className="text-sm text-text-secondary">{getListSubtitle(list)}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={getStatusBadge(list.status)}>
                        {list.status}
                      </span>
                      {list.items.length > 0 && (
                        <button
                          onClick={() => handlePublishList(list)}
                          className="flex items-center space-x-2 px-3 py-1 bg-primary-500 text-white text-sm rounded hover:bg-primary-600"
                        >
                          <Send className="w-3 h-3" />
                          <span>Publish</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {renderListItems(list, true)}
                {list.items.length > 0 && (
                  <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <button
                      onClick={() => handleAddItem(list)}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-gray-200 text-text-secondary rounded-lg hover:bg-gray-100"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Another Book</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Archived (passive) Lists */}
      {passiveLists.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-text-primary mb-4">Archived Lists</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {passiveLists.map(list => (
              <div key={list.id} className="bg-bg-surface rounded-lg border border-gray-200 opacity-90">
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-text-primary">
                        {new Date(list.month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                      </h3>
                      <p className="text-sm text-text-secondary">{getListSubtitle(list)}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={getStatusBadge(list.status)}>
                        {list.status}
                      </span>
                      <button
                        onClick={() => handlePublishList(list)}
                        className="flex items-center space-x-1 px-2 py-1 text-xs bg-primary-500 text-white rounded hover:bg-primary-600"
                        title="Republish"
                      >
                        <Send className="w-3 h-3" />
                        <span>Republish</span>
                      </button>
                    </div>
                  </div>
                </div>
                {renderListItems(list, false)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {displayLists.length === 0 && (
        <div className="bg-bg-surface rounded-lg border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Filter className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-text-primary mb-2">No New Releases Lists</h3>
          <p className="text-text-secondary mb-4">No New Releases lists have been created yet.</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            Create First List
          </button>
        </div>
      )}

      {/* Create List Modal */}
      <CreateListModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleListCreated}
      />

      {/* Item Modal */}
      <NewReleasesItemModal
        isOpen={showItemModal}
        onClose={() => {
          setShowItemModal(false);
          setEditingItem(null);
          setCurrentListContext(null);
        }}
        item={editingItem}
        listContext={currentListContext}
        onSuccess={loadLists}
      />
    </div>
  );
};

export default NewReleases;
